import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of, race } from 'rxjs';
import { catchError, map, switchMap, tap, withLatestFrom, filter, take } from 'rxjs/operators';
import { PageableModel, VentilationParameter } from '@mona/models';
import {
    applyInstancesChanges,
    ChangeLogAction,
    ChangeLogSelectors,
    ChangeLogState,
    getChangesData,
    getPersistedChangesData,
    getRevertedChangesForModel,
    getRevertedCreateIds,
    reconcileEntityUpdatesWithChanges,
} from '@mona/pdms/data-access-changelog';
import { currentEncounterId, isEmptyEncounterId, withCurrentEncounterId } from '@mona/pdms/data-access-combined';
import { Logger } from '@mona/shared/logger';
import { isSameEncounterFilter } from '@mona/shared/utils';
import { selectCurrentRoute, selectRouteData } from '@mona/store';
import { VentilationParametersApi } from '../../infrastructure';
import { VentilationAction } from '../actions';
import { selectVentilationParameters } from '../selectors';

/**
 * Ventilation effects
 */
@Injectable()
export class VentilationEffects {
    private logger = new Logger();
    /**
     * Load ventilation parameters effect.
     *
     * Uses a `race` against `isEmptyEncounterId` so that an in-flight load that gets
     * cancelled because the selected encounter was cleared (typical during logout /
     * login navigation flicker, or rapid encounter switching) still emits a terminal
     * action (`loadVentilationParametersCancelled`). The previous implementation used
     * `takeUntil(isEmptyEncounterId(...))` which silently completed the inner observable
     * without dispatching anything — `loadVentilationParameters` had already flipped
     * `isLoading` to `true`, and the ventilation-table mat-card `[uiLoadingOverlay]`
     * (bound to `selectVentilationIsLoading`) buried any subsequent cell-edit save
     * click until something else happened to reset the flag (e.g. the
     * disconnect → discard → cancel-rfid sequence the colleague onsite hit).
     * Customer reproducer from a hospital terminal. Mirrors the fix already
     * applied to `loadDailyGoals$`, `loadProcedurePrescriptions$` and
     * `loadLabValues$` for the same MD-9750 stuck-spinner family.
     */
    loadVentilationParameters$ = createEffect(() =>
        this.actions$.pipe(
            ofType(VentilationAction.loadVentilationParameters),
            withCurrentEncounterId(),
            switchMap(([{ params, url }, encounter_id]) =>
                race(
                    isEmptyEncounterId(this.store$).pipe(
                        take(1),
                        map(() =>
                            VentilationAction.loadVentilationParametersCancelled({
                                reason: 'encounter-cleared',
                            }),
                        ),
                    ),
                    this.ventilationParametersApi
                        .getPageableVentilationParameters({ ...params, encounter_id }, url)
                        .pipe(
                            isSameEncounterFilter(currentEncounterId(this.store$), encounter_id),
                            catchError(error => {
                                this.logger.error('Error loading VentilationParameter', error);
                                return of({ results: [], codes: [] } as PageableModel<VentilationParameter>);
                            }),
                            withLatestFrom(this.store$.select(ChangeLogSelectors.getChangesMap)),
                            map(([data, changesMap]) => {
                                const changes = changesMap['VentilationParameter'] || [];

                                if (changes.length) {
                                    data.results = applyInstancesChanges<VentilationParameter>(
                                        data.results,
                                        changes,
                                        true,
                                    );
                                }

                                return VentilationAction.loadVentilationParametersSuccess(data);
                            }),
                            catchError(error => of(VentilationAction.loadVentilationParametersFailed({ error }))),
                        ),
                ),
            ),
        ),
    );

    // On savechange action toggle loading to avoid multiple documentations on slow network
    onSaveChangeActionToggleLoading$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ChangeLogAction.saveChangeAction.action),
                withLatestFrom(this.store$.select(selectCurrentRoute)),
                filter(([action, route]) => route && route.url?.includes('ventilation')),
                tap(() => this.store$.dispatch(VentilationAction.setLoadingAction({ isLoading: true }))),
            ),
        { dispatch: false },
    );

    /** Handle save failures - disable loading state */
    onChangeSaveFailed$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ChangeLogAction.saveChangeAction.failedAction),
                tap(() => this.store$.dispatch(VentilationAction.setLoadingAction({ isLoading: false }))),
            ),
        { dispatch: false },
    );

    /** Listen save change success & add one - realistic update */
    onChangeSaved$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(
                    ChangeLogAction.saveChangeAction.action,
                    ChangeLogAction.saveChangeAction.succeededAction,
                    ChangeLogAction.saveChangesAction.action,
                    ChangeLogAction.saveChangesAction.succeededAction,
                    ChangeLogAction.revertOptimisticChange,
                    ChangeLogAction.revertOptimisticChanges,
                    ChangeLogAction.revertStaleSavingEntries,
                ),
                withLatestFrom(
                    this.store$.select(selectVentilationParameters),
                    this.store$.select(ChangeLogSelectors.getChangesMap),
                ),
                tap(([action, parameters, changesMap]) => {
                    const parameterChanges = changesMap['VentilationParameter'] || [];
                    const reverted = getRevertedChangesForModel(action, 'VentilationParameter');
                    const data = reconcileEntityUpdatesWithChanges(parameters, parameterChanges, reverted);

                    const revertedCreateIds = getRevertedCreateIds(reverted);
                    if (revertedCreateIds.length) {
                        this.store$.dispatch(VentilationAction.removeVentilationParameters({ ids: revertedCreateIds }));
                    }
                    if (data.length) {
                        this.store$.dispatch(VentilationAction.upsertVentilationParameters({ data }));
                    }
                    this.store$.dispatch(VentilationAction.setLoadingAction({ isLoading: false }));
                }),
            ),
        { dispatch: false },
    );

    /** Listen change persist success & add one - realistic update */
    onPersistChangeSuccess$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ChangeLogAction.persistChangesAction.succeededAction),
                withLatestFrom(
                    this.store$.select(ChangeLogSelectors.getChangesMap),
                    this.store$.select(selectVentilationParameters),
                ),
                tap(([, changesMap, parameters]) => {
                    const parameterChanges = changesMap['VentilationParameter'] || [];

                    if (parameterChanges.length) {
                        const { toRemove, toUpdate } = getPersistedChangesData(parameters, parameterChanges);

                        this.store$.dispatch(
                            VentilationAction.addParameterChanges({
                                toUpdateEntities: toUpdate || [],
                                toRemoveIds: toRemove.map(({ id }) => id) || [],
                            }),
                        );
                    }
                }),
            ),
        { dispatch: false },
    );

    /**
     * Constructor
     * @param actions$ Actions
     * @param ventilationParametersApi VentilationParametersApi
     * @param store$
     */
    constructor(
        private actions$: Actions,
        private ventilationParametersApi: VentilationParametersApi,
        private store$: Store<{ changelogData: ChangeLogState }>,
    ) {}
}
